import React, { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTransitionPresence } from "../hooks/useTransitionPresence.js";
import { cn } from "../lib/utils.js";
import { MOTION_DURATION_MS, type MotionPhase } from "../motion.js";
import { ShellRightRailOccupiedContext } from "../lib/shell-right-rail-context.js";
import { getLayerRoot } from "./layer-stack.js";

/* OverlaySurface — the ONE anchored-overlay chassis (ADR-0091).

   `ModalShell` already does this for modal layers. Anchored surfaces —
   listboxes, menus, pickers, calendars — had no equivalent, so each one was
   written as `{open && createPortal(<ul>…)}`: it appears in a single frame and
   is destroyed in a single frame, and no CSS can give a destroyed element an
   exit. This extracts the contract that `Popover` already implements correctly
   so every anchored surface shares one implementation.

   The contract is three things, and the third is the one that is easy to miss:
     1. the node stays mounted for its closing frame,
     2. it carries `data-motion-state`, which the motion utilities read,
     3. while closing it is `inert` and `aria-hidden` — visually gone means gone
        to a screen reader and to the Tab key too, not just to the eye. */

export type OverlayPreset =
  | "anchored"
  | "anchored-panel"
  | "docked-panel"
  | "dialog"
  | "sheet-end"
  | "sheet-bottom";

const PRESET_CLASS: Record<OverlayPreset, string> = {
  anchored: "pb-motion-origin-anchored",
  "anchored-panel": "pb-motion-origin-anchored-panel",
  "docked-panel": "pb-motion-origin-docked-panel",
  dialog: "pb-motion-origin-dialog",
  "sheet-end": "pb-motion-origin-sheet-end",
  "sheet-bottom": "pb-motion-origin-sheet-bottom",
};

export interface OverlayPresenceOptions {
  /** Where it travels from; `anchored` is the menu/listbox default. */
  readonly preset?: OverlayPreset;
  readonly exitDurationMs?: number;
  /**
   * The surface node. Given one, the hook keeps `inert` in step with the
   * closing phase — React 18 serialises `inert={false}` as the string
   * `"false"`, which HTML reads as inert, so it can only be toggled here.
   */
  readonly ref?: React.RefObject<HTMLElement | null>;
}

export interface OverlayPresence {
  /** Render the surface while this is true — it stays true through the exit. */
  readonly present: boolean;
  readonly phase: MotionPhase;
  /** True on the first closing frame: stop committing, stop repositioning. */
  readonly closing: boolean;
  /** Spread onto the surface node; put your own className after it. */
  readonly motionProps: {
    readonly "data-motion-state": MotionPhase;
    readonly className: string;
    readonly "aria-hidden"?: "true";
  };
}

/**
 * Presence plus the motion attributes for one anchored overlay.
 *
 * Use this rather than {@link OverlaySurface} when the caller owns its portal
 * or needs `present` to drive its own positioning — a position must keep being
 * computed (or simply retained) while the surface closes, or it will fly to the
 * top-left corner on its way out.
 */
export function useOverlayPresence(
  open: boolean,
  options: OverlayPresenceOptions = {},
): OverlayPresence {
  const {
    preset = "anchored",
    exitDurationMs = MOTION_DURATION_MS.state,
    ref,
  } = options;
  const { present, phase } = useTransitionPresence(open, exitDurationMs);
  const closing = phase === "closing";

  useLayoutEffect(() => {
    ref?.current?.toggleAttribute("inert", closing);
  }, [closing, present, ref]);

  return {
    present,
    phase,
    closing,
    motionProps: {
      "data-motion-state": phase,
      className: cn(
        preset === "docked-panel" ? "pb-motion-rail" : "pb-motion-overlay",
        PRESET_CLASS[preset],
      ),
      ...(closing ? { "aria-hidden": "true" as const } : {}),
    },
  };
}

export interface OverlaySurfaceProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  open: boolean;
  preset?: OverlayPreset;
  exitDurationMs?: number;
  /** Element to render — a listbox wants "ul", a menu "div". */
  as?: "div" | "ul" | "section" | "aside";
  /** Portal target; defaults to the shared layer root. */
  container?: Element | null;
  children: React.ReactNode;
}

/**
 * A portalled overlay that animates both ways.
 *
 * Positioning stays with the caller (via `style`), because every anchored
 * surface measures its own trigger.
 */
export function OverlaySurface({
  open,
  preset = "anchored",
  exitDurationMs,
  as = "div",
  container,
  className,
  children,
  ...rest
}: OverlaySurfaceProps) {
  const ref = useRef<HTMLElement | null>(null);
  const { present, motionProps } = useOverlayPresence(open, {
    preset,
    ref,
    ...(exitDurationMs === undefined ? {} : { exitDurationMs }),
  });
  if (!present) return null;
  if (typeof document === "undefined") return null;

  const Element = as as React.ElementType;
  const node = (
    <Element
      ref={ref}
      {...motionProps}
      {...rest}
      className={cn(motionProps.className, className)}
    >
      {/* Portalled out of Shell's side rail: an overlay is sized by the
          window, not by main (PB-5436). */}
      <ShellRightRailOccupiedContext.Provider value={false}>
        {children}
      </ShellRightRailOccupiedContext.Provider>
    </Element>
  );
  return createPortal(node, container ?? getLayerRoot());
}
